import { Fragment, useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

import { lireSession, rafraichirSession } from '../auth/session';
import { Bouton } from '../components/Boutons';

/**
 * Assistant Claude du back-office : chaque conversation est une session Claude Code dérivée de la
 * session de développement du projet (service genviz-assistant, dossier assistant/ du dépôt).
 * Lecture seule, confinée au dossier du projet ; réservé aux administrateurs.
 */

interface Message {
  role: 'user' | 'assistant';
  texte: string;
  outils?: string[];
  at: string;
  auteur?: string;
}
interface Resume {
  id: string;
  titre: string;
  majLe: string;
  nbMessages: number;
}
interface Conversation {
  id: string;
  titre: string;
  messages: Message[];
  enCours?: boolean;
}

const BASE = '/assistant-api';

async function appel(chemin: string, init: RequestInit = {}, deja = false): Promise<Response> {
  const r = await fetch(BASE + chemin, {
    ...init,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${lireSession().jetonAcces ?? ''}`, ...init.headers },
  });
  if (r.status === 403 && !deja && (await rafraichirSession())) return appel(chemin, init, true);
  return r;
}

// ---------- rendu Markdown minimal (sans HTML injecté) ----------
function enLigne(texte: string): ReactNode[] {
  const morceaux: ReactNode[] = [];
  const re = /(`[^`]+`|\*\*[^*]+\*\*|(?<![\w*])_[^_]+_(?!\w)|(?<![\w*])\*[^*\s][^*]*\*(?!\w))/g;
  let dernier = 0;
  let m: RegExpExecArray | null;
  let k = 0;
  while ((m = re.exec(texte))) {
    if (m.index > dernier) morceaux.push(texte.slice(dernier, m.index));
    const t = m[0];
    if (t.startsWith('`')) morceaux.push(<code key={k++} style={styles.codeLigne}>{t.slice(1, -1)}</code>);
    else if (t.startsWith('**')) morceaux.push(<strong key={k++}>{t.slice(2, -2)}</strong>);
    else morceaux.push(<em key={k++}>{t.slice(1, -1)}</em>);
    dernier = m.index + t.length;
  }
  if (dernier < texte.length) morceaux.push(texte.slice(dernier));
  return morceaux;
}

function Markdown({ texte }: { texte: string }): JSX.Element {
  const lignes = texte.split('\n');
  const blocs: ReactNode[] = [];
  let i = 0;
  let k = 0;
  const ligne = (j: number): string => lignes[j] ?? '';
  while (i < lignes.length) {
    const l = ligne(i);
    if (l.startsWith('```')) {
      const code: string[] = [];
      i++;
      while (i < lignes.length && !ligne(i).startsWith('```')) code.push(ligne(i++));
      i++;
      blocs.push(<pre key={k++} style={styles.codeBloc}>{code.join('\n')}</pre>);
    } else if (/^#{1,4} /.test(l)) {
      const niveau = l.indexOf(' ');
      blocs.push(<div key={k++} style={{ fontWeight: 600, fontSize: niveau <= 2 ? 16 : 14, margin: '10px 0 4px' }}>{enLigne(l.slice(niveau + 1))}</div>);
      i++;
    } else if (/^\s*\|/.test(l)) {
      const rangees: string[][] = [];
      while (i < lignes.length && /^\s*\|/.test(ligne(i))) {
        const cellules = ligne(i).trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
        if (!cellules.every((c) => /^:?-{2,}:?$/.test(c))) rangees.push(cellules);
        i++;
      }
      blocs.push(
        <div key={k++} style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <tbody>
              {rangees.map((r, ri) => (
                <tr key={ri}>
                  {r.map((c, ci) => (ri === 0
                    ? <th key={ci} style={styles.cellule}>{enLigne(c)}</th>
                    : <td key={ci} style={styles.cellule}>{enLigne(c)}</td>))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
    } else if (/^\s*([-*]|\d+\.) /.test(l)) {
      const ordonnee = /^\s*\d+\. /.test(l);
      const items: { niveau: number; texte: string }[] = [];
      while (i < lignes.length && /^\s*([-*]|\d+\.) /.test(ligne(i))) {
        const m = /^(\s*)([-*]|\d+\.) (.*)$/.exec(ligne(i))!;
        items.push({ niveau: (m[1] ?? '').length >= 2 ? 1 : 0, texte: m[3] ?? '' });
        i++;
      }
      const Liste = ordonnee ? 'ol' : 'ul';
      blocs.push(
        <Liste key={k++} style={{ margin: '4px 0', paddingLeft: 22 }}>
          {items.map((it, j) => <li key={j} style={{ marginLeft: it.niveau * 18 }}>{enLigne(it.texte)}</li>)}
        </Liste>,
      );
    } else if (!l.trim()) {
      i++;
    } else {
      const para: string[] = [];
      while (i < lignes.length && ligne(i).trim() && !/^(```|#{1,4} |\s*\||\s*([-*]|\d+\.) )/.test(ligne(i))) para.push(ligne(i++));
      blocs.push(
        <p key={k++} style={{ margin: '4px 0' }}>
          {para.map((p, j) => <Fragment key={j}>{j > 0 && <br />}{enLigne(p)}</Fragment>)}
        </p>,
      );
    }
  }
  return <>{blocs}</>;
}

// ---------- page ----------
export function AssistantPage(): JSX.Element {
  const [liste, setListe] = useState<Resume[]>([]);
  const [conv, setConv] = useState<Conversation | null>(null);
  const [saisie, setSaisie] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [outilCourant, setOutilCourant] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const fil = useRef<HTMLDivElement>(null);

  const chargerListe = useCallback(async () => {
    const r = await appel('/conversations');
    if (r.ok) setListe(await r.json());
    else setErreur((await r.json().catch(() => ({}))).erreur ?? `erreur ${r.status}`);
  }, []);

  const ouvrir = useCallback(async (id: string) => {
    const r = await appel(`/conversations/${id}`);
    if (r.ok) setConv(await r.json());
  }, []);

  useEffect(() => { void chargerListe(); }, [chargerListe]);
  useEffect(() => { fil.current?.scrollTo({ top: fil.current.scrollHeight }); }, [conv, outilCourant]);

  const nouvelle = async (): Promise<Conversation | null> => {
    const r = await appel('/conversations', { method: 'POST' });
    if (!r.ok) return null;
    const c: Conversation = await r.json();
    setConv(c);
    void chargerListe();
    return c;
  };

  const envoyer = async (): Promise<void> => {
    const texte = saisie.trim();
    if (!texte || enCours) return;
    setErreur(null);
    const cible = conv ?? (await nouvelle());
    if (!cible) return;
    setSaisie('');
    setEnCours(true);
    const question: Message = { role: 'user', texte, at: new Date().toISOString() };
    const reponse: Message = { role: 'assistant', texte: '', outils: [], at: new Date().toISOString() };
    setConv({ ...cible, messages: [...cible.messages, question, reponse] });
    const maj = (): void => setConv((c) => (c ? { ...c, messages: [...c.messages.slice(0, -1), { ...reponse }] } : c));
    try {
      const r = await appel(`/conversations/${cible.id}/messages`, { method: 'POST', body: JSON.stringify({ texte }) });
      if (!r.ok || !r.body) throw new Error((await r.json().catch(() => ({}))).erreur ?? `erreur ${r.status}`);
      const lecteur = r.body.getReader();
      const dec = new TextDecoder();
      let tampon = '';
      for (;;) {
        const { done, value } = await lecteur.read();
        if (done) break;
        tampon += dec.decode(value, { stream: true });
        let n;
        while ((n = tampon.indexOf('\n')) >= 0) {
          const e = JSON.parse(tampon.slice(0, n));
          tampon = tampon.slice(n + 1);
          if (e.type === 'texte') { reponse.texte += e.texte; setOutilCourant(null); maj(); }
          else if (e.type === 'outil') { reponse.outils!.push(e.resume); setOutilCourant(e.resume); maj(); }
          else if (e.type === 'fin') { Object.assign(reponse, e.message); maj(); }
        }
      }
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e));
      void ouvrir(cible.id);
    } finally {
      setEnCours(false);
      setOutilCourant(null);
      void chargerListe();
    }
  };

  const arreter = (): void => { if (conv) void appel(`/conversations/${conv.id}/arreter`, { method: 'POST' }); };
  const supprimer = async (id: string): Promise<void> => {
    if (!window.confirm('Supprimer cette conversation ?')) return;
    await appel(`/conversations/${id}`, { method: 'DELETE' });
    if (conv?.id === id) setConv(null);
    void chargerListe();
  };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div>
        <h1 style={{ margin: 0 }}>Assistant</h1>
        <p style={{ margin: '4px 0 0', color: 'var(--gv-texte-2)' }}>
          Questions sur le fonctionnement du backend, du firmware, des mesures et du labo. L’assistant
          connaît l’historique du développement et lit le code du projet. Il ne modifie rien, sauf
          ses notes (docs/notes-assistant/, partagées entre les conversations : « note que… »), et
          peut interroger la base de données en lecture seule (mesures, trames, impulsions…).
        </p>
      </div>
      <div style={styles.cadre}>
        <aside style={styles.liste}>
          <Bouton variante="primaire" pleineLargeur onClick={() => { setConv(null); setSaisie(''); }}>
            Nouvelle conversation
          </Bouton>
          {liste.map((c) => (
            <div key={c.id} style={{ ...styles.entree, ...(conv?.id === c.id ? styles.entreeActive : {}) }}>
              <button type="button" style={styles.entreeBouton} onClick={() => void ouvrir(c.id)} title={c.titre}>
                <div style={styles.entreeTitre}>{c.titre}</div>
                <div style={{ fontSize: 12, color: 'var(--gv-texte-3)' }}>
                  {new Date(c.majLe).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                </div>
              </button>
              <button type="button" aria-label="Supprimer" style={styles.supprimer} onClick={() => void supprimer(c.id)}>×</button>
            </div>
          ))}
        </aside>
        <section style={styles.discussion}>
          <div ref={fil} style={styles.fil}>
            {!conv?.messages.length && (
              <div style={{ color: 'var(--gv-texte-2)', margin: 'auto', textAlign: 'center', maxWidth: 460 }}>
                Pose une question, par exemple : « Comment sont calculés les kV d’une impulsion ? »,
                « Tension moyenne par jour de la carte 115 cette semaine ? » ou « Pourquoi la carte 115 ne remontait plus sa télémétrie le 22/10 ? ».
                <br /><br />La première réponse d’une conversation prend un peu plus de temps (reprise de tout l’historique).
              </div>
            )}
            {conv?.messages.map((m, i) => (
              <div key={i} style={m.role === 'user' ? styles.bulleQuestion : styles.bulleReponse}>
                {m.role === 'user' ? <div style={{ whiteSpace: 'pre-wrap' }}>{m.texte}</div> : (
                  <>
                    {!!m.outils?.length && (
                      <details style={{ fontSize: 12, color: 'var(--gv-texte-3)', marginBottom: 4 }}>
                        <summary>{m.outils.length} lecture{m.outils.length > 1 ? 's' : ''} du projet</summary>
                        {m.outils.map((o, j) => <div key={j} style={{ fontFamily: 'var(--gv-police-mono)' }}>{o}</div>)}
                      </details>
                    )}
                    {m.texte ? <Markdown texte={m.texte} /> : (
                      <div style={{ color: 'var(--gv-texte-3)' }}>{outilCourant ? `${outilCourant}…` : 'réflexion…'}</div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
          {erreur && <div style={{ color: 'var(--gv-rouge)', padding: '0 12px' }}>{erreur}</div>}
          <div style={styles.saisie}>
            <textarea
              value={saisie}
              onChange={(e) => setSaisie(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void envoyer(); } }}
              placeholder="Ta question… (Entrée pour envoyer, Maj+Entrée pour aller à la ligne)"
              rows={3}
              style={styles.zone}
            />
            {enCours
              ? <Bouton onClick={arreter}>Arrêter</Bouton>
              : <Bouton variante="primaire" disabled={!saisie.trim()} onClick={() => void envoyer()}>Envoyer</Bouton>}
          </div>
        </section>
      </div>
    </div>
  );
}

const styles = {
  cadre: { display: 'grid', gridTemplateColumns: 'minmax(180px, 260px) 1fr', gap: 12, height: 'calc(100vh - 230px)', minHeight: 480 },
  liste: { display: 'flex', flexDirection: 'column', gap: 4, overflowY: 'auto', minWidth: 0 },
  entree: { display: 'flex', alignItems: 'stretch', border: '1px solid var(--gv-bordure)', borderRadius: 6, background: 'var(--gv-surface)' },
  entreeActive: { borderColor: 'var(--gv-vert)', background: 'var(--gv-vert-doux)' },
  entreeBouton: { flex: 1, minWidth: 0, textAlign: 'left', background: 'none', border: 'none', padding: '6px 8px', cursor: 'pointer', color: 'inherit', font: 'inherit' },
  entreeTitre: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 13 },
  supprimer: { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gv-texte-3)', fontSize: 16, padding: '0 8px' },
  discussion: { display: 'flex', flexDirection: 'column', border: '1px solid var(--gv-bordure)', borderRadius: 8, background: 'var(--gv-surface)', minWidth: 0, minHeight: 0 },
  fil: { flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 10 },
  bulleQuestion: { alignSelf: 'flex-end', maxWidth: '80%', background: 'var(--gv-vert-doux)', borderRadius: 10, padding: '8px 12px' },
  bulleReponse: { alignSelf: 'stretch', maxWidth: '100%', padding: '4px 4px', lineHeight: 1.5, overflowWrap: 'anywhere' },
  saisie: { display: 'flex', gap: 8, alignItems: 'flex-end', padding: 12, borderTop: '1px solid var(--gv-bordure)' },
  zone: { flex: 1, resize: 'vertical', font: 'inherit', padding: 8, borderRadius: 6, border: '1px solid var(--gv-bordure-forte)', background: 'var(--gv-surface)', color: 'inherit' },
  codeLigne: { fontFamily: 'var(--gv-police-mono)', fontSize: '0.9em', background: 'var(--gv-neutre-doux)', padding: '0 4px', borderRadius: 3 },
  codeBloc: { fontFamily: 'var(--gv-police-mono)', fontSize: 12, background: 'var(--gv-neutre-doux)', padding: 8, borderRadius: 6, overflowX: 'auto', margin: '6px 0' },
  table: { borderCollapse: 'collapse', fontSize: 13, margin: '6px 0' },
  cellule: { border: '1px solid var(--gv-bordure)', padding: '3px 8px', textAlign: 'left', verticalAlign: 'top' },
} as const satisfies Record<string, CSSProperties>;
